import { useContext, useEffect, useState } from 'react';
import { Redirect, Route, BrowserRouter as Router, Switch } from 'react-router-dom';

import { AppContainer, EditContainer, OnboardingContainer, SignOutContainer, SmartMeterContainer } from '../containers';
import { AppContext } from '../context/AppContext';
import ContextProvider from '../context/ContextProvider';
import { useActiveSubscription, useSystemFeatures } from '../hooks';
import useAuthDomainPrefixGuard from '../hooks/useAuthDomainPrefixGuard';
import { getCurrentUserInfo } from '../utils/auth';

import { AppGate } from './AppGate';

interface UserAttributes {
  [key: string]: string | undefined;
}

interface OptimiseUser {
  attributes?: UserAttributes;
}

const HomeRedirect: React.FC<{ isOnboarded: boolean }> = ({ isOnboarded }) =>
  isOnboarded ? <Redirect to="/app" /> : <Redirect to="/onboarding" />;

const AppRouterWithContext: React.FC = () => {
  useSystemFeatures();
  useAuthDomainPrefixGuard({
    requireClaim: true,
    redirectOnMismatch: true,
  });

  const { isOnboarded, setIsOnboarded, subscriptionsDisabled, currentUserInfo, setCurrentUserInfo, isAdmin } =
    useContext(AppContext) as {
      isOnboarded: boolean;
      setIsOnboarded: (v: boolean) => void;
      subscriptionsDisabled: boolean;
      currentUserInfo: OptimiseUser | undefined;
      setCurrentUserInfo: (currentUserInfo: OptimiseUser | undefined) => void;
      isAdmin: boolean;
    };
  const { activeSubscription, fetchedSubscriptions } = useActiveSubscription();
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const fetchUserInfo = async () => {
      let userInfo;
      try {
        if (!!currentUserInfo?.attributes && currentUserInfo.attributes['custom:is_onboarded'] === 'true') {
          userInfo = currentUserInfo;
        } else {
          setLoading(true);
          userInfo = (await getCurrentUserInfo()) as OptimiseUser | undefined;
          setCurrentUserInfo(userInfo);
        }
        if (cancelled) return;
        const onboarded = !!userInfo?.attributes && userInfo.attributes['custom:is_onboarded'] === 'true';
        setIsOnboarded(onboarded);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void fetchUserInfo();
    return () => {
      cancelled = true;
    };
  }, [setIsOnboarded]);

  // Unified gating logic
  const isLoading = loading || (!subscriptionsDisabled && !fetchedSubscriptions);
  const hasActiveSubscription = subscriptionsDisabled || !!activeSubscription;
  const canAccessApp = isOnboarded && hasActiveSubscription;
  const canAccessOnboarding = !isOnboarded || !hasActiveSubscription;
  const needsSubscription = !isAdmin && isOnboarded && !subscriptionsDisabled && !activeSubscription;

  const guardContext = {
    loading: isLoading,
    canAccessApp,
    canAccessOnboarding,
    needsSubscription,
  };

  return (
    <Router>
      <AppGate ctx={guardContext}>
        <Switch>
          <Route path="/onboarding" component={OnboardingContainer} />
          <Route path="/app" component={AppContainer} />
          <Route path="/smart-meter" component={SmartMeterContainer} />
          <Route path="/edit" component={EditContainer} />
          <Route path="/sign-out" component={SignOutContainer} />
          <Route path="/" exact render={() => <HomeRedirect isOnboarded={isOnboarded} />} />
        </Switch>
      </AppGate>
    </Router>
  );
};

const AppRouter = () => (
  <ContextProvider>
    <AppRouterWithContext />
  </ContextProvider>
);

export default AppRouter;
